import { useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  EmptyState,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { useJournalActions, usePlantJournalEntries } from "~/state/journal";
import { spacing } from "~/styles/theme";
import type { JournalEntryKind } from "~/api/types";

const kindOptions: { value: JournalEntryKind; label: string }[] = [
  { value: "note", label: "Note" },
  { value: "watered", label: "Watered" },
  { value: "fed", label: "Fed" },
  { value: "repotted", label: "Repotted" },
  { value: "flowered", label: "Flowered" },
  { value: "pest", label: "Pest" },
  { value: "pruned", label: "Pruned" },
  { value: "moved", label: "Moved" },
  { value: "other", label: "Other" },
];

const kindLabels = Object.fromEntries(
  kindOptions.map((option) => [option.value, option.label]),
) as Record<JournalEntryKind, string>;

function formatOccurredAt(value: string) {
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return value;
  }
}

export function PlantJournal({ plantId }: { plantId: string }) {
  const entries = usePlantJournalEntries(plantId);
  const actions = useJournalActions();
  const [kind, setKind] = useState<JournalEntryKind>("note");
  const [body, setBody] = useState("");

  function addEntry() {
    actions.create(plantId, { kind, body });
    setBody("");
    setKind("note");
  }

  return (
    <Stack gap="md">
      {entries.length === 0 ? (
        <EmptyState
          title="No journal entries yet"
          description="Log watering, feeding, pests, and anything else worth remembering."
        />
      ) : (
        <Stack gap="sm">
          {entries.map((entry) => (
            <View key={entry.id} style={stylesheet.entry}>
              <View style={stylesheet.entryHeader}>
                <AppText size="sm" weight="semibold">
                  {kindLabels[entry.kind]}
                </AppText>
                <AppText size="xs">
                  {formatOccurredAt(entry.occurred_at)}
                </AppText>
              </View>
              {entry.body ? <AppText size="sm">{entry.body}</AppText> : null}
              <Button
                variant="text"
                compact
                align="start"
                onPress={() => actions.remove(entry.id)}
              >
                <ButtonText>Delete</ButtonText>
              </Button>
            </View>
          ))}
        </Stack>
      )}

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Kind
        </AppText>
        <SearchableSelect
          title="Choose journal kind"
          accessibilityLabel="Journal entry kind"
          value={kind}
          options={kindOptions}
          placeholder="Note"
          onValueChange={(value) => setKind(value as JournalEntryKind)}
        />
      </View>
      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Entry
        </AppText>
        <TextField
          accessibilityLabel="Journal entry"
          value={body}
          placeholder="What happened?"
          multiline
          style={stylesheet.textarea}
          onChangeText={setBody}
        />
      </View>
      <Button align="start" iconName="plus" onPress={addEntry}>
        <ButtonText>Add journal entry</ButtonText>
      </Button>
    </Stack>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
  textarea: { minHeight: 72, textAlignVertical: "top" },
  entry: { gap: spacing.xs },
  entryHeader: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: spacing.xs,
  },
});
